import {ParentComponentProvider} from "../../../context/ParentComponentContext";
import C027Quote from "../../content/C027Quote/C027Quote";
import C087LandingPageStageTeaser from "../../content/C087LandingPageStageTeaser/C087LandingPageStageTeaser";
import F008PeriodIncreaseForm from "../../forms/F008PeriodIncreaseForm/F008PeriodIncreaseForm";
import CCOArticle from "../../fundamentals/CCOArticle";
import React, {useEffect, useMemo, useState} from "react";
import C036SearchAction from "../../content/C036SearchField/C036SearchAction";
import {useQuery} from "@apollo/client";
import {
    GetCollectionByIdDocument
} from "../../../../packages/graphql-layer/src/queries/__generated__/GetCollectionById";
import {
    GetCmPlaceholderByIdDocument
} from "../../../../packages/graphql-layer/src/queries/__generated__/GetCMPlaceholderById";
import {GetCmPictureByIdDocument} from "../../../../packages/graphql-layer/src/queries/__generated__/GetCMPictureById";
import {RichTextElement} from "../../fundamentals/CCORichText/CCORichTextHelper";
import CCOComment from "../../fundamentals/CCOComment";
import C077LobsterRequestSimpleAction from "../../content/C077LobsterRequest/C077LobsterRequestSimpleAction";
import {getCypressDataProperty} from "../../../utility/componentUtil";
import C029TextFragment from "../../content/C029TextFragment/C029TextFragment";
import {
    CmArticleType,
    CmCollectionType,
    CmHTMLType,
    CmPictureType,
    CmPlaceholderType,
    CmVideoType,
    FormEditorType,
    SosAmountIncreaseFormType,
    SosContactType,
    SosDonationCardType,
    SosDonationFormType,
    SosDonationProductType,
    SosDonationProgressType,
    SosDonationToolType,
    SosOrderFormType,
    SosOverlayType,
    SosTeaserType,
    SosTextfragmentType
} from "../../types";
import CollectionViewType from "./CollectionViewType";
import {hideForBreakpoint, useBreakpoints} from "../../../utility/layoutUtils";
import dynamic from "next/dynamic";
import Box from "@mui/material/Box";
import C082CenteredStageTeaser from "../../content/C082CenteredStageTeaser/C082CenteredStageTeaser";
import C014StageTeaser from "../../content/C014StageCarousel/C014StageTeaser";
import C088HelveticaStageTeaser from "../../content/C088HelveticaStageTeaser/C088HelveticaStageTeaser";

import type {CAEGlobalSettings} from "../../../types/CAEGlobalSettings";
import C051BankData from "../../content/C051BankData/C051BankData";

interface Props {
    CAEGlobalSettings: CAEGlobalSettings,
    csrfToken: string,
    isOverlayPreview?: boolean,
    isPreview?: boolean,
    item: any,
    pageSettings?: any,
    placeholderContent?: any,
    renderPictureSizes?: boolean,
    richTextItemId?: string,
    credentials?: any,
    donationInitializerData?: { queryData: any; isProduct: boolean; contentId: string } | null
}

const C085ProductTeaserContainer = dynamic(
    () => import('../../content/C085ProductTeaser/C085ProductTeaserContainer'));
const C003Download = dynamic(() => import('../../content/C003Download/C003Download'));
const C041Video = dynamic(() => import('../../content/C041Video/C041Video'));
const C040Picture = dynamic(() => import('../../content/C040Picture/C040Picture'));
const C067HtmlFragment = dynamic(() => import('../../content/C067HtmlFragment/C067HtmlFragment'));
// Client-only on purpose: the initializer derives the whole form shape from sessionStorage
// (product form data, aborted-payment state), which does not exist on the server - server-rendering
// it produces markup the first client render cannot match, breaking hydration for the entire page.
// A donation form is interactive and carries no crawlable content, so nothing is lost for SEO.
const CCODonationInitializer = dynamic(() => import('../../fundamentals/CCODonationInitializer'),
    {ssr: false});
const C023NewsletterTeaser = dynamic(
    () => import('../../content/C023NewsletterTeaser/C023NewsletterTeaser'));
const C018StandardTeaser = dynamic(
    () => import('../../content/C018StandardTeaser/C018StandardTeaser'));
const C028Infobox = dynamic(() => import('../../content/C028Infobox/C028Infobox'));
const C022ContactTeaser = dynamic(() => import('../../content/C022ContactTeaser/C022ContactTeaser'));
const C016GlobalFilter = dynamic(() => import('../../content/C016GlobalFilter/C016GlobalFilter'),
    {ssr: false});
const C031JobTeaserPlaceholder = dynamic(
    () => import('../../content/C031JobTeaserPlaceholder/C031JobTeaserPlaceholder'), {ssr: false});
const C036PublicationSearchAction = dynamic(
    () => import('../../content/C036SearchField/C036PublicationSearchAction'), {ssr: false});
const C024CTATeaser = dynamic(() => import('../../content/C024CTATeaser/C024CTATeaser'));
const C062OverlayForm = dynamic(() => import('../../content/C062OverlayForm/C062OverlayForm'));
const C060OverlayTeaser = dynamic(() => import('../../content/C060OverlayTeaser/C060OverlayTeaser'));
const C061OverlayContact = dynamic(
    () => import('../../content/C061OverlayContact/C061OverlayContact'));
const C080DonationProgress = dynamic(
    () => import('../../content/C080DonationProgress/C080DonationProgress'));
const C081DonationTool = dynamic(() => import('../../content/C081DonationTool/C081DonationTool'));
const F005OrderForm = dynamic(() => import('../../forms/F005OrderForm/F005OrderForm'),
    {ssr: false});
const C056QueryList = dynamic(() => import('../../content/C056QueryList/C056QueryList'),
    {ssr: false});
const C046FormEditor = dynamic(() => import('../../content/C046FormEditor/C046FormEditor'),
    {ssr: false});
const F006AmountIncreaseForm = dynamic(
    () => import('../../forms/F006AmountIncreaseForm/F006AmountIncreaseForm'), {ssr: false});
const C094CallToActionCard = dynamic(
    () => import('../../content/C094CallToActionCard/C094CallToActionCard'));

function PlacementItem(props: Props) {

    const [richTextItem, setRichTextItem] = useState(null);
    const currentBreakpoint = useBreakpoints();

    useEffect(() => {
    }, [richTextItem]);

    const queryDocument = useMemo(() => {
        if (props.item.type === "CMPicture") {
            return GetCmPictureByIdDocument;
        }

        if (props.item.type === "CMPlaceholder" && props.richTextItemId) {
            return GetCmPlaceholderByIdDocument;
        }

        return GetCollectionByIdDocument;
    }, [props.item.type, props.richTextItemId]);

    const {data, error} = useQuery(queryDocument, {
        variables: {contentId: props.richTextItemId},
        fetchPolicy: 'network-only',
        skip: props.richTextItemId === null || props.richTextItemId === undefined,
    });

    useEffect(() => {
        if (!error) return;
        console.error("Can't load collection:", error);
    }, [error]);

    useEffect(() => {
        if (!data) return;
        // Ensure expected shape is present before updating state
        const content = (data as any).content?.content;
        if (!content) {
            console.error("Can't load collection:");
            return;
        }
        setRichTextItem(content);
    }, [data]);

    const item = richTextItem ?? props.item

    if (hideForBreakpoint(currentBreakpoint, item.responsiveSettings)) {
        return null
    }
    switch (item.type) {

        case 'CMCollection': {
            const collection = item as CmCollectionType
            return <CollectionViewType item={collection}
                                       CAEGlobalSettings={props.CAEGlobalSettings}
                                       richTextItemId={props.richTextItemId}/>
        }
        case "FormEditor": {
            const form = item as FormEditorType
            return <CCOComment text='form.C046FormEditor'><C046FormEditor drymode={false}
                                                                          formEditor={form}
                                                                          id={form.id}
                                                                          csrfToken={props.csrfToken}
                                                                          pageSettings={props.pageSettings}
                                                                          fcSiteKey={props.credentials?.friendlyCaptchaId}
                                                                          layoutSettings={item.layoutSettings}/></CCOComment>
        }
        case "SOSTextfragment": {
            const sosTextFragmentItem = item as SosTextfragmentType
            switch (sosTextFragmentItem.viewtype) {
                case "c027_quote": {
                    return <CCOComment text='textfragment.C027Quote'><ParentComponentProvider
                        id={sosTextFragmentItem.id}><C027Quote
                        sosTextFragment={sosTextFragmentItem}/></ParentComponentProvider></CCOComment>
                }
                case "c028_infobox": {
                    return <CCOComment text='textfragment.C028Infobox'><ParentComponentProvider
                        id={sosTextFragmentItem.id}><C028Infobox
                        sosTextFragment={sosTextFragmentItem}/></ParentComponentProvider></CCOComment>
                }
                default: {
                    return <CCOComment text='textfragment.C029TextFragment'><ParentComponentProvider
                        id={sosTextFragmentItem.id}><C029TextFragment
                        sosTextFragment={item}/></ParentComponentProvider></CCOComment>
                }
            }
        }
        case "CMHTML": {
            const cmHtml = item as CmHTMLType
            return <CCOComment text='html.C067HtmlFragment'><C067HtmlFragment cmHtml={cmHtml}
                                                                              isPreview={props.isPreview}/></CCOComment>
        }
        case "CMVideo": {
            const video = item as CmVideoType
            return <CCOComment text='video.C041Video'><C041Video
                layoutSettings={item.layoutSettings} video={video}/></CCOComment>
        }
        case "CMDownload": {
            return <CCOComment text='cmCollection.C003Download'><C003Download
                cmCollection={{items: [item]} as CmCollectionType}/></CCOComment>
        }
        case "CMPicture": {
            const picture = item as CmPictureType
            if (!picture || !picture.id) return null

            return <CCOComment text='picture.C040Picture'><C040Picture picture={picture}
                                                                       isPreview={props.renderPictureSizes}
                                                                       isRichText={props.richTextItemId !==
                                                                           undefined}
                                                                       clazz={props.item.clazz}/></CCOComment>
        }
        case "SOSDonationCard": {
            const card = item as SosDonationCardType;
            const supportedViewtypes = ["c083_modular_donation_card", "c084_modular_donation_card_sticky"];

            if (supportedViewtypes.includes(card.viewtype ?? "")) {
                return <CCODonationInitializer
                    key={card.id}
                    item={card}
                    csrfToken={props.csrfToken}
                    donationInitializerData={props.donationInitializerData ?? null}
                />
            }
            return null;
        }
        case "SOSDonationForm": {
            if (item.viewtype === 'f007_modular_donation_form') {
                const donationForm = item as SosDonationFormType;
                return (
                    <Box sx={{minHeight: '1000px', width: '100%'}}>
                        <CCODonationInitializer
                            item={donationForm}
                            csrfToken={props.csrfToken}
                            donationInitializerData={props.donationInitializerData ?? null}
                        />
                    </Box>
                );
            }
            return null;
        }
        case "SOSAmountIncreaseForm": {
            const sosAmountIncreaseForm = item as SosAmountIncreaseFormType;
            if (sosAmountIncreaseForm.viewtype === "f008_period_increase_form") {
                return <CCOComment text='item.F008PeriodIncreaseForm'>
                    <React.Fragment key="periodIncreaseItem">
                        {sosAmountIncreaseForm?.donationContext?.donation_context && <F008PeriodIncreaseForm
                            id={sosAmountIncreaseForm.id}
                            sosAmountIncreaseForm={sosAmountIncreaseForm}
                            csrfToken={props.csrfToken}
                            donationContext={item.donationContext.donation_context as any}
                        />}
                    </React.Fragment>
                </CCOComment>
            }
            return <CCOComment text='item.F006AmountIncreaseForm'>
                <React.Fragment key="amountIncreaseItem">
                    {item?.donationContext?.donation_context && <F006AmountIncreaseForm
                        id={item.id}
                        sosAmountIncreaseForm={sosAmountIncreaseForm}
                        csrfToken={props.csrfToken}
                        donationContext={item.donationContext.donation_context as any}
                    />}
                </React.Fragment>
            </CCOComment>
        }

        case "SOSDonationProgress": {
            const donationProgress = item as SosDonationProgressType;
            return <CCOComment text='item.C080DonationProgress'>
                <React.Fragment key="donationProgress">
                    <C080DonationProgress
                        actionId={""}
                        id={donationProgress.id}
                        item={donationProgress}
                        csrfToken={props.csrfToken}
                    />
                </React.Fragment>
            </CCOComment>
        }
        case "SOSDonationTool": {
            const donationTool = item as SosDonationToolType;
            return <CCOComment text='item.C081DonationTool'>
                <React.Fragment key="donationTool">
                    <C081DonationTool
                        id={donationTool.id}
                        item={donationTool}
                        csrfToken={props.csrfToken}
                    />
                </React.Fragment>
            </CCOComment>
        }
        case "SOSOrderForm": {
            const orderForm = item as SosOrderFormType;
            return <CCOComment text='item.F005OrderForm'>
                <React.Fragment key="orderFormItem">
                    <F005OrderForm
                        id={item.id}
                        orderForm={orderForm}
                        csrfToken={props.csrfToken}
                    />
                </React.Fragment>
            </CCOComment>
        }
        case "SOSTeaser": {
            const sosTeaser = item as SosTeaserType;
            switch (item.viewtype) {
                case "c023_newsletter_teaser": {
                    return <CCOComment text='teaser.C023NewsletterTeaser'><ParentComponentProvider
                        id={sosTeaser.id}><C023NewsletterTeaser
                        sosTeaser={sosTeaser}/></ParentComponentProvider></CCOComment>
                }
                case "c024_cta_teaser": {
                    return <CCOComment text='teaser.C024CTATeaser'><ParentComponentProvider
                        id={sosTeaser.id}><C024CTATeaser sosTeaser={sosTeaser}
                                                         layoutSettings={sosTeaser.layoutSettings}/></ParentComponentProvider></CCOComment>
                }
                case "c094_call_to_action_card": {
                    return <CCOComment text='teaser.C094CallToActionCard'><ParentComponentProvider
                        id={sosTeaser.id}><C094CallToActionCard
                        sosTeaser={sosTeaser}/></ParentComponentProvider></CCOComment>
                }
                case "c082_centered_stage_teaser": {
                    return <CCOComment
                        text='teaser.C082CenteredStageTeaser'><ParentComponentProvider
                        id={sosTeaser.id}><C082CenteredStageTeaser
                        item={sosTeaser}/></ParentComponentProvider></CCOComment>
                }
                case "c087_landing_page_stage_teaser": {
                    return <CCOComment
                        text='teaser.CO87LandingPageStageTeaser'><ParentComponentProvider
                        id={sosTeaser.id}><C087LandingPageStageTeaser
                        item={sosTeaser}/></ParentComponentProvider></CCOComment>
                }
                case "c088_helvetica_stage_teaser": {
                    return <CCOComment
                        text='teaser.CO88HelveticaStageTeaser'><ParentComponentProvider
                        id={sosTeaser.id}><C088HelveticaStageTeaser
                        item={sosTeaser}/></ParentComponentProvider></CCOComment>
                }
                default: {
                    return <>
                        <CCOComment text='teaser.C014StageTeaser'><ParentComponentProvider
                            id={sosTeaser.id}><C014StageTeaser
                            item={sosTeaser}/></ParentComponentProvider></CCOComment>
                    </>
                }
            }

        }
        case "SOSDonationProduct": {
            const items = [item as SosDonationProductType];
            return <CCOComment text='item.C085ProductTeaserContainer'><C085ProductTeaserContainer
                items={items} key={item.id}/></CCOComment>
        }
        case "CMQueryList": {
            return <CCOComment text='item.C056QueryList'><C056QueryList queryList={item}
                                                                        viewType={item.viewtype}/></CCOComment>
        }
        case "SOSContact": {
            const sosContact = item as SosContactType;
            return <CCOComment text='item.C022ContactTeaser'><C022ContactTeaser
                contactTeaser={sosContact}/></CCOComment>
        }
        case "CMArticle": {
            const article = item as CmArticleType;
            return <CCOComment text='item.CCOArticle'><ParentComponentProvider
                id={article.id}><CCOArticle item={article}
                                            layoutSettings={article.layoutSettings}
                                            CAEGlobalSettings={props.CAEGlobalSettings}/></ParentComponentProvider></CCOComment>
        }
        case "CMPlaceholder": {
            const cmPlaceholder = item as CmPlaceholderType;
            switch (cmPlaceholder.viewtype) {
                case "c016_global_filter": {
                    return <CCOComment text='item.C016GlobalFilter'><C016GlobalFilter
                        specialTopicCategory={props!.placeholderContent}/></CCOComment>
                }
                case "c031_job_teaser_placeholder": {
                    return <CCOComment
                        text='item.C031JobTeaserPlaceholder'><C031JobTeaserPlaceholder
                        placeholder={cmPlaceholder}/></CCOComment>
                }
                case "c051_bankdata": {
                    return <CCOComment text='item.C051BankData'><C051BankData
                        CAEGlobalSettings={props.CAEGlobalSettings}/></CCOComment>
                }
                default: {
                    return <CCOComment text='item.C018StandardTeaser'><C018StandardTeaser
                        dataCypress={getCypressDataProperty(cmPlaceholder) + 'StandardTeaser'}
                        cmTeaser={cmPlaceholder} displayDate={false}/></CCOComment>
                }
            }
        }
        case "SOSOverlay": {
            const sosOverlay = item as SosOverlayType;
            switch (sosOverlay.viewtype) {
                case "c060_overlay_teaser": {
                    return <CCOComment text='item.C060OverlayTeaser'>
                        <C060OverlayTeaser breakpoints={sosOverlay.autoShowOverlayInBreakpoints}
                                           details={sosOverlay.details!.textAsTree as RichTextElement}
                                           id={sosOverlay.id}
                                           isPreview={props.isOverlayPreview}
                                           picture={sosOverlay.picture}
                                           title={sosOverlay.title}
                                           linksTarget={sosOverlay.linkTargets}
                                           itemId={sosOverlay.id}/>
                    </CCOComment>
                }
                case "c061_overlay_contact": {
                    return sosOverlay.details ? <CCOComment text='item.C061OverlayContact'>
                        <C061OverlayContact breakpoints={sosOverlay.autoShowOverlayInBreakpoints}
                                            id={sosOverlay.id}
                                            details={sosOverlay.details!.textAsTree as RichTextElement}
                                            isPreview={props.isOverlayPreview}
                                            picture={sosOverlay.picture}
                                            subtitle={sosOverlay.subtitle}
                                            targets={sosOverlay.linkTargets}
                                            title={sosOverlay.title}
                        /></CCOComment> : null
                }
                case "c062_overlay_form": {
                    return <CCOComment text='item.C062OverlayForm'>
                        <C062OverlayForm breakpoints={sosOverlay.autoShowOverlayInBreakpoints}
                                         id={sosOverlay.id}
                                         formData={sosOverlay.items && sosOverlay.items[0]}
                                         isPreview={props.isOverlayPreview}
                                         pageSettings={props.pageSettings}
                                         picture={sosOverlay.picture}
                                         subtitle={sosOverlay.subtitle}
                                         title={sosOverlay.title}
                                         token={props.csrfToken}
                                         fcSiteKey={props.credentials?.friendlyCaptchaId}/>
                    </CCOComment>
                }
                default: {
                    return <CCOComment text='item.C018StandardTeaser'><C018StandardTeaser
                        dataCypress={getCypressDataProperty(sosOverlay) + 'StandardTeaser'}
                        cmTeaser={sosOverlay} displayDate={false}/></CCOComment>
                }
            }
        }
        default: {
            switch (item.viewtype) {
                case "Standard": {
                    // News Teaser
                    return <CCOComment text='item.C018StandardTeaser'><C018StandardTeaser
                        dataCypress={getCypressDataProperty(item) + 'StandardTeaser'}
                        cmTeaser={item} displayDate={true}/></CCOComment>
                }
                case "search-all": {
                    return <CCOComment text='item.C036SearchAction'><C036SearchAction/></CCOComment>
                }
                case "search-publication": {
                    return <CCOComment
                        text='item.C036PublicationSearchAction'><C036PublicationSearchAction/></CCOComment>
                }
                case "lobster-request-simple": {
                    return <CCOComment
                        text='item.C077LobsterRequestSimpleAction'><C077LobsterRequestSimpleAction
                        cmAction={item} csrfToken={props.csrfToken}/></CCOComment>
                }
                default: {
                    return <CCOComment text='item.C018StandardTeaser'><C018StandardTeaser
                        dataCypress={getCypressDataProperty(item) + 'StandardTeaser'}
                        cmTeaser={item} displayDate={false}/></CCOComment>
                }
            }
        }
    }
}

export default PlacementItem